import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Sparkles, LogOut, ArrowLeft, BarChart3, ShoppingBag, Package, Users, Shield, Store, Settings, PartyPopper, FolderTree, PackageSearch, Wallet, ClipboardList, TrendingUp, IndianRupee,
} from "lucide-react";

const ADMIN_NAV = [
  { key: "stats", label: "Overview", icon: BarChart3 },
  { key: "bookings", label: "Bookings", icon: ShoppingBag },
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "profit", label: "Profit", icon: TrendingUp },
  { key: "costs", label: "Costs", icon: IndianRupee },
  { key: "vendors", label: "Vendors", icon: Store },
  { key: "categories", label: "Categories", icon: FolderTree },
  { key: "sourcing", label: "Sourcing", icon: PackageSearch },
  { key: "budget-tiers", label: "Budget Tiers", icon: Wallet },
  { key: "products", label: "Products", icon: Package },
  { key: "experiences", label: "Experiences", icon: PartyPopper },
  { key: "users", label: "Users", icon: Users },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeTab = params.get("tab") || "stats";

  const switchTab = (k) => setParams({ tab: k }, { replace: true });
  const exitAdmin = () => navigate("/dashboard");
  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-linen flex flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col bg-charcoal text-white sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-brand">
            <div className="w-10 h-10 rounded-2xl rose-glow flex items-center justify-center shadow-lg shadow-rose-500/40">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <p className="font-display text-lg leading-none">CelebrAI</p>
              <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-rose-300 mt-1 flex items-center gap-1">
                <Shield className="w-3 h-3" strokeWidth={2} /> Admin
              </p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {ADMIN_NAV.map((n) => {
            const isActive = activeTab === n.key;
            return (
              <button key={n.key} onClick={() => switchTab(n.key)} data-testid={`admin-nav-${n.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${
                  isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                }`}>
                <n.icon className="w-4 h-4" strokeWidth={isActive ? 2.25 : 1.75} />
                {n.label}
                {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full rose-glow" />}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white/20" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-400 to-violet-500 flex items-center justify-center text-sm font-bold text-white">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-[10px] text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={exitAdmin} data-testid="exit-admin-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Exit to user view
          </button>
          <button onClick={doLogout} data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition">
            <LogOut className="w-4 h-4" strokeWidth={1.75} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden bg-charcoal text-white sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={exitAdmin} data-testid="mobile-exit-admin" className="p-2 -ml-2 rounded-full hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl rose-glow flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" strokeWidth={2} />
              </div>
              <p className="font-display text-lg leading-none">Admin</p>
            </div>
          </div>
          <button onClick={doLogout} data-testid="mobile-admin-logout" className="p-2 -mr-2 rounded-full hover:bg-white/10">
            <LogOut className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>
        <div className="overflow-x-auto border-t border-white/10">
          <div className="flex gap-1 px-3 py-2">
            {ADMIN_NAV.map((n) => {
              const isActive = activeTab === n.key;
              return (
                <button key={n.key} onClick={() => switchTab(n.key)} data-testid={`admin-mnav-${n.key}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                    isActive ? "bg-white text-charcoal" : "text-white/70 bg-white/5"
                  }`}>
                  <n.icon className="w-3.5 h-3.5" strokeWidth={2} />
                  {n.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10 overflow-x-hidden">{children}</main>
    </div>
  );
}
